import type { Task } from '@/data/types';
import { daysOverdue } from './dates';
import { parseExpiryDetail } from './fleetDocs';
import { documentKindKey } from './loadDocs';

type T = (key: string, values?: Record<string, unknown>) => string;

/**
 * Every value of `public.task_source` (0002, 0039, 0046, 0085, 0143). The SQL writers store an ENGLISH title
 * and detail (load_reject 0040, app.enqueue_dunning 0049, delete_my_account 0086/0093), so the portal
 * renders each machine source from copy; only `manual` keeps its stored title, which the office typed.
 */
export const TASK_SOURCES = [
  'manual',
  'document_expiry',
  'load_rejected',
  'invoice_overdue',
  'driver_account_deleted',
  // invoice-pdf (0143): the weekly invoice was not emailed (the close found no address, or the send
  // failed), and the broker invoice email failed.
  'invoice_delivery',
  'broker_invoice_delivery',
  // fleet-driver-pay (0282, 0284): a truck's service plan is due soon or overdue (app.sweep_maintenance_due).
  'maintenance_due',
  // audit-b-receivables (0305/0306): a broker receivable past its due date (app.sweep_receivable_overdue).
  'receivable_overdue',
  // audit-b-docs (0310/0314): a delivered load with no POD past the agency's hours (app.sweep_pod_missing).
  'pod_missing',
  // audit-d-office (0325/0326): a load was canceled; an informational task for its dispatcher.
  'load_canceled',
] as const;

// The detail formats of the writers (format() strings in SQL). A detail that does not match falls back
// to copy without the parsed value, never to the English text.
const DELETED_DRIVER = /^(.+?) deleted their Coreway app account\./; // delete_my_account
const OVERDUE = /^Invoice (.+) is (\d+) day\(s\) overdue/; // app.enqueue_dunning
const NOT_EMAILED = /^Invoice (.+) was not emailed$/; // app.invoice_send_core, the Friday close (0146)
const EMAIL_FAILED = /^Invoice (.+) email failed$/; // app.document_email_mark_failed (0146)
// app.sweep_maintenance_due (0284): `{label} on truck {unit}: {n} mi left` (n clamped at 0 when overdue) or
// `{label} on truck {unit}: due {YYYY-MM-DD}`; the title is `Maintenance overdue` or `Maintenance due soon`.
const MAINT_MILES = /^(.+?) on truck (.+): (\d+) mi left$/;
const MAINT_DATE = /^(.+?) on truck (.+): due (\d{4}-\d{2}-\d{2})$/;
const MAINT_OVERDUE = 'Maintenance overdue';
// app.sweep_receivable_overdue (0306): `Load {number}: {broker | Unknown broker} owes ${balance} since {due_on}`. No day
// count on purpose (F1): a daily change would rewrite the task and re-ping the bell every morning. The portal counts
// the days from {due_on} to today in the agency zone.
const RECV_OVERDUE = /^Load (.+?): (.+) owes \$([\d,]+\.\d{2}) since (\d{4}-\d{2}-\d{2})$/;
const UNKNOWN_BROKER = 'Unknown broker';
// app.sweep_pod_missing (0314): `Load {number} delivered {hours} h ago, no POD yet`, written at each raise (the hours
// are those of the raise; the task is rewritten only when it reopens, every 48 h at most).
const POD_MISSING = /^Load (.+) delivered (\d+) h ago, no POD yet$/;

/** What a line needs to count days (receivable_overdue): the agency zone (unknown -> no count) and now (tests). */
export interface TaskTitleClock {
  timeZone?: string;
  now?: Date;
}

/** The line the Upcoming Tasks card shows for a task, in the UI language. */
export function taskTitle(
  task: Pick<Task, 'source' | 'title' | 'detail' | 'loadId'>,
  loadNumber: string | null,
  t: T,
  clock: TaskTitleClock = {},
): string {
  switch (task.source) {
    case 'load_rejected':
      if (!loadNumber) return t('tasks.sources.loadRejectedGeneric');
      return task.detail
        ? t('tasks.sources.loadRejected', { load: loadNumber, reason: task.detail })
        : t('tasks.sources.loadRejectedNoReason', { load: loadNumber });
    case 'load_canceled':
      if (!loadNumber) return t('tasks.sources.loadCanceledGeneric');
      return task.detail
        ? t('tasks.sources.loadCanceled', { load: loadNumber, reason: task.detail })
        : t('tasks.sources.loadCanceledNoReason', { load: loadNumber });
    case 'driver_account_deleted': {
      const driver = task.detail?.match(DELETED_DRIVER)?.[1] ?? null;
      if (!loadNumber) return t('tasks.sources.driverAccountDeletedGeneric');
      return driver
        ? t('tasks.sources.driverAccountDeleted', { load: loadNumber, driver })
        : t('tasks.sources.driverAccountDeletedNoDriver', { load: loadNumber });
    }
    case 'invoice_overdue': {
      const m = task.detail?.match(OVERDUE);
      return m ? t('tasks.sources.invoiceOverdue', { invoice: m[1], count: Number(m[2]) }) : t('tasks.sources.invoiceOverdueGeneric');
    }
    case 'invoice_delivery': {
      const none = task.title.match(NOT_EMAILED);
      if (none) return t('tasks.sources.invoiceNotEmailed', { invoice: none[1] });
      const failed = task.title.match(EMAIL_FAILED);
      return failed ? t('tasks.sources.invoiceEmailFailed', { invoice: failed[1] }) : t('tasks.sources.invoiceDeliveryGeneric');
    }
    case 'broker_invoice_delivery': {
      const failed = task.title.match(EMAIL_FAILED);
      if (!failed) return t('tasks.sources.brokerInvoiceDeliveryGeneric');
      return loadNumber
        ? t('tasks.sources.brokerInvoiceEmailFailed', { invoice: failed[1], load: loadNumber })
        : t('tasks.sources.brokerInvoiceEmailFailedNoLoad', { invoice: failed[1] });
    }
    case 'document_expiry': {
      // app.sweep_document_expiry (0128): `{owner_kind} {owner}: {kind} {expires|expired} on {date}`.
      const d = parseExpiryDetail(task.detail);
      if (!d) return t('tasks.sources.documentExpiry');
      return t(d.expired ? 'tasks.sources.documentExpired' : 'tasks.sources.documentExpiring', {
        kind: t(documentKindKey(d.kind)),
        owner: d.ownerName,
        date: d.date,
      });
    }
    case 'maintenance_due': {
      const overdue = task.title === MAINT_OVERDUE;
      const miles = task.detail?.match(MAINT_MILES);
      if (miles) {
        const values = { label: miles[1], truck: miles[2], miles: Number(miles[3]).toLocaleString('en-US') };
        return t(overdue ? 'tasks.sources.maintenanceMilesOverdue' : 'tasks.sources.maintenanceMilesLeft', values);
      }
      const date = task.detail?.match(MAINT_DATE);
      if (date) {
        return t(overdue ? 'tasks.sources.maintenanceWasDue' : 'tasks.sources.maintenanceDueOn', { label: date[1], truck: date[2], date: date[3] });
      }
      return t(overdue ? 'tasks.sources.maintenanceOverdueGeneric' : 'tasks.sources.maintenanceDueGeneric');
    }
    case 'receivable_overdue': {
      const m = task.detail?.match(RECV_OVERDUE);
      if (!m) return t('tasks.sources.receivableOverdueGeneric');
      const values = {
        load: m[1],
        broker: m[2] === UNKNOWN_BROKER ? t('tasks.sources.unknownBroker') : m[2],
        amount: `$${m[3]}`,
        date: m[4],
      };
      // Whole days past the due date in the agency's calendar; without the zone (or not yet a day) the since date only.
      const days = clock.timeZone ? daysOverdue(m[4], clock.timeZone, clock.now) : 0;
      return days >= 1
        ? t('tasks.sources.receivableOverdue', { ...values, count: days })
        : t('tasks.sources.receivableOverdueSince', values);
    }
    case 'pod_missing': {
      const m = task.detail?.match(POD_MISSING);
      return m ? t('tasks.sources.podMissing', { load: m[1], hours: m[2] }) : t('tasks.sources.podMissingGeneric');
    }
    default:
      // `manual`, and a source newer than this build: the stored title.
      return task.title;
  }
}
